/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Circle,
  FormControl,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  SimpleGrid,
  Spinner,
} from '@chakra-ui/react';
import {
  FormCQSelect,
  FormTextInput,
  useCQMediaQuery,
} from '@laxmimanogna/code-quick-components';
import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import AppColors from '../../../constants/AppColors';
import {
  AddIcon,
  HealthSystemIcon,
  PersonIcon,
} from '../../../constants/IconData';
import { FONT_FAMILY } from '../../../constants/Theme';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SelectFormModal from './SelectFormModal';
import FormMultiSelectModal from '../../../components/form/FormMultiSelect';
import { AccountContext } from '../../../providers/AccountsProvider';
import { separateIdNames } from '../../../utils';
import { useNavigate } from 'react-router-dom';
import { replaceRoute } from '../../../utils/common.utils';
import ROUTES from '../../../constants/Routes';
import accountsRepository from '../../../repositories/AccountsRepository';
import { STATES, headingValidationRegex, onlyAlphabetsRegex } from '../../../constants/constants';
import SearchDropDown from '../components/SearchDropDown';
import AccountContactInput from './components/AccountContactInput';

const AddHospitalScheme = yup.object({
  name: yup
    .string()
    .required('Name is a required field')
    .matches(headingValidationRegex, 'Name must be valid'),
  address: yup.string(),
  patients_per_month: yup
    .string()
    .matches(/^\d+$/, 'Only Numbers allowed ')
    .required('Patients per month is a required field'),
  account_contact: yup.array().of(
    yup.object().shape({
      first_name: yup
        .string()
        .required('First name is required')
        .matches(onlyAlphabetsRegex, 'Only letters (no edge spaces)'),
      last_name: yup
        .string()
        .required('Last name is required')
        .matches(onlyAlphabetsRegex, 'Only letters (no edge spaces)'),
      email: yup
        .string().email("Enter valid email")
        .nullable(),
    })
  ),
});

const GRID_SPACING = 5;

function createNewContact() {
  return {
    first_name: '',
    last_name: '',
    email: '',
    is_primary: false,
    can_upload: true,
    unique_id: '',
  };
}

const AddHospitalForm = ({
  isOpen,
  onClose,
  hospitalId,
  editHospitalId,
  accountId,
  healthSystemId,
  hsAddress,
  setHsState,
}) => {
  // context
  const accountsContext = useContext(AccountContext);
  const [selectedPrimaryAccountCheckBoxIndex, spacbiState] = useState([]);
  const [selectedCanUploadCheckBoxIndex, setSelectedCanUploadCheckBoxIndex] = useState([]);
  const [hospitalState, setHospitalState] = useState('');

  const [dataValue, setDataValue] = useState({});
  const navigate = useNavigate();
  const [parentSpocList, setParentSpocList] = useState(null);

  const blockScrollOnMount = useCQMediaQuery({
    sm: false,
  });

  const form = useForm({
    resolver: yupResolver(AddHospitalScheme),
    defaultValues: {
      account_contact: [createNewContact()],
    },
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = form;

  const contactField = useFieldArray({
    control,
    name: 'account_contact',
  });

  const scrollContainerRef = useRef(null);

  function scrollToBottom() {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }

  useEffect(() => {
    if (editHospitalId) {
      reset({ ...accountsContext.hospitalData });
      accountsContext.fetchHospital(editHospitalId).then(res => {
        setValue(
          'specialties',
          res.specialty.map(s => s.id)
        );
        setValue(
          'ehr',
          res.ehr.map(s => s.id)
        );
        setValue(
          'insurance',
          res.insurance.map(s => s.id)
        );
        setHospitalState(res.address);
        setValue('address', res.address);
      });
    }
  }, [editHospitalId]);

  const fetchParentSpocList = async () => {
    const resp = await accountsRepository.getClientSpocList({
        health_system_id: healthSystemId,
        // hospital_id: 319,
        check_availability: true
      });
    setParentSpocList(resp);
  }

  useEffect(() => {
    const healthSystemData = accountsContext.healthSystem;
    
    if (Object.keys(healthSystemData).length) {      
      setValue(
        'specialties',
        healthSystemData.specialty.map(s => s.id)
      );
      setValue(
        'ehr',
        healthSystemData.ehr.map(s => s.id)
      );
      setValue(
        'insurance',
        healthSystemData.insurance.map(s => s.id)
      );
      setHospitalState(healthSystemData.address);
      setValue('address', healthSystemData.address);
    }
  }, [accountsContext.healthSystem]);

  useEffect(() => {
    const initialSelectedIndexes = [];
    getValues('account_contact')?.forEach((dt, index) => {
      if (index === 0) {
        initialSelectedIndexes.push(index);
        setValue(`account_contact.${index}.can_upload`, true);
      }
    });
    setSelectedCanUploadCheckBoxIndex(initialSelectedIndexes);
  }, []);

  useEffect(() => {
    getValues('account_contact')?.forEach((dt, index) => {
      if (selectedPrimaryAccountCheckBoxIndex.includes(index)) {
        setValue(`account_contact.${index}.is_primary`, true);
      } else {
        setValue(`account_contact.${index}.is_primary`, false);
      }
    });
  }, [selectedPrimaryAccountCheckBoxIndex]);

  useEffect(() => {
    getValues('account_contact')?.forEach((dt, index) => {
      if (selectedCanUploadCheckBoxIndex.includes(index)) {
        setValue(`account_contact.${index}.can_upload`, true);
      } else {
        setValue(`account_contact.${index}.can_upload`, false);
      }
    });
    scrollToBottom();
  }, [selectedCanUploadCheckBoxIndex]);

  useEffect(() => {
    if (dataValue && Object.keys(dataValue).length) {
      if (
        accountsContext.specialtyOptions.findIndex(
          d => d.id === dataValue.id && d.label === dataValue.label
        ) === -1
      ) {
        accountsContext.setSpecialtyOptions(prev => [...prev, dataValue]);
      }
    }
  }, [dataValue]);

  const handleCheckBox = (event, index) => {
    let checkedIndexes = [...selectedPrimaryAccountCheckBoxIndex];
    if (event.target.checked) {
      checkedIndexes.push(index);
    } else {
      checkedIndexes = checkedIndexes.filter(d => d !== index);
    }
    spacbiState(checkedIndexes);
  };

  const handleCheckBoxUpload = (event, index) => {
    let checkedIndexes = [...selectedCanUploadCheckBoxIndex];
    if (event.target.checked) {
      checkedIndexes.push(index);
    } else {
      checkedIndexes = checkedIndexes.filter(d => d !== index);
    }
    setSelectedCanUploadCheckBoxIndex(checkedIndexes);
  };

  useEffect(() => {
    loadOptions();
    accountsContext.cqHospitalsData();
  }, []);

  /** --- DATA FETCHING --- */
  function loadOptions() {
    accountsContext.fetchEHROptions();
    accountsContext.fetchInsuranceOptions();
    accountsContext.fetchSpecialtyOptions();
  }

  const addHospital = hospitalBody => {
    const isHospitalExist = accountsContext.hospitalDatas?.find(h => {
      if (h?.name === hospitalBody.name) {
        setError('name', {
          type: 'manual',
          message: `${hospitalBody.name} already exists`,
        });
        return h?.name;
      }
    });
    if (!isHospitalExist) {
      accountsContext
        .createHospital({
          ...hospitalBody,
          address: hospitalState?.split(',')?.at(-1)?.trim(),
        })
        .then(res => {
          if (res) {
            const hospitalEditPage = replaceRoute(ROUTES.GET_HOSPITAL, {
              accountId,
              hospitalId: res?.id,
            });
            navigate(hospitalEditPage);
            reset({
              account_contact: [createNewContact()],
            });
            onClose();
          }
        });
    }
  };

  /** --- DATA HANDLING --- */
  async function submitAddAccount(formData) {
    const hospitalBody = {
      name: formData.name,
      address: formData.address?.split(',').at(-1).trim(),
      patients_per_month: formData.patients_per_month,
      specialties: separateIdNames(formData.specialties, 'new_spec_name'),
      insurance: separateIdNames(formData.insurance, 'new_ins_name'),
      ehr: separateIdNames(formData.ehr, 'new_ehr_name'),
      account_contact: formData.account_contact,
      health_system: accountId,
    };

    for (let idx = 0; idx < hospitalBody.account_contact.length; idx++) {
      if (!isNewAccountContact[idx]) {
        hospitalBody.account_contact[idx]['parent_spoc'] = true;
      }
    } 

    if (editHospitalId) {
      accountsContext.updateHospital(editHospitalId, hospitalBody).then(res => {
        const hospitalEditPage = replaceRoute(ROUTES.GET_HOSPITAL, {
          accountId,
          hospitalId: res.id,
        });
        navigate(hospitalEditPage);
        onClose();
      });
    } else {
      addHospital(hospitalBody);
    }
  }

  function renderAddIcon(_props) {
    return (
      <HStack mr={12} mb={3} _hover={{ cursor: 'pointer' }} {..._props}>
        <Text fontSize={'sm'} fontWeight={'bold'} color={AppColors.secondary}>
          Add
        </Text>
        <AddIcon style={{ width: 18, height: 18 }} />
      </HStack>
    );
  }


  const [emailOptions, emailToIndexMapping] = useMemo(() => {
    const emailOpt = [], emailToIndMap = {};
    if (parentSpocList) {
      parentSpocList.map((option, index) => {
        // Use "first_name-last_name" as the key when email is null
        const key = option.email || `${option.first_name}-${option.last_name}`;
        emailOpt.push(key);
        emailToIndMap[key] = index;
      })
      return [emailOpt, emailToIndMap];
    } 
    return [null, null];
  }, [parentSpocList]);


  const [availableEmailOptions, setAvailableEmailOptions] = useState(null);
  const [isNewAccountContact, setIsNewAccountContact] = useState(null);
  const [selectedEmails, setSelectedEmails] = useState(null);

  useEffect(() => {
    if (parentSpocList) {
      const curEmailOpt = []
      parentSpocList.map((option, index) => {
        // Show "first_name-last_name" when email is null
        const displayLabel = option.email || `${option.first_name}-${option.last_name}`;
        curEmailOpt.push({label: displayLabel, value: index});
      })
      setAvailableEmailOptions(curEmailOpt);
    } else {
      setAvailableEmailOptions(null);
    }
    setIsNewAccountContact([false]);
    setSelectedEmails([{label: null, value: null}]);
  }, [parentSpocList]);

  

  const resetAccountContactMetadata = () => {
    // this in turn will make emailOptions, emailToIndexMapping, availableEmailOptions, isNewAccountContact
    setParentSpocList(null);
  }

  const editArrayValue = (arr, index, val) => {
    const updated = [...arr];
    updated[index] = val;
    return updated;
  }

  const editFirstAndLastName = (index, newFirstName, newLastName) => {
    setValue(`account_contact.${index}.first_name`, newFirstName);
    setValue(`account_contact.${index}.last_name`, newLastName);
  }

  function renderContacts() {
    // return <Box>{contactField.fields.map(renderAddContactForm)}</Box>;
    return <AccountContactInput
            fetchParentSpocList={fetchParentSpocList}
            onChange={async (selectedOption, field, index) => {
              // add the previous value to the currentEmailOptions if it was previously there
              if (emailOptions.includes(selectedEmails[index].label)) {
                setAvailableEmailOptions(prev => {
                  const alreadyExists = prev.some(item => item.value === selectedEmails[index].value);
                  if (!alreadyExists) {
                    return [...prev, selectedEmails[index]];
                  }
                  return prev;
                });
              }

              // Check for duplicate emails
              if (selectedOption && selectedEmails.map(item => item.label).filter(item => item != null).includes(selectedOption?.label)) {
                setIsNewAccountContact(prev => editArrayValue(prev, index, false));
                editFirstAndLastName(index, '', '');
                setError(`account_contact.${index}.email`, {
                  type: 'email',
                  message: 'Cannot repeat email'
                })
                return;
              }

              // Handle new email vs existing email
              const isNewEmail = selectedOption?.__isNew__ ?? false;
              
              setIsNewAccountContact(prev => {
                const updated = [...prev];
                updated[index] = isNewEmail;
                
                if (selectedOption && !isNewEmail) {
                  // Existing email from parent SPOC list
                  const selectedSpoc = parentSpocList[selectedOption?.value];
                  editFirstAndLastName(index, selectedSpoc.first_name, selectedSpoc.last_name);
                  setValue(`account_contact.${index}.unique_id`, selectedSpoc.unique_id);
                } else {
                  // New email - clear first/last name so user can enter them
                  editFirstAndLastName(index, '', '');
                  setValue(`account_contact.${index}.unique_id`, '');
                }
                return updated;
              })

              // SET EMAIL VALUE
              if (isNewEmail) {
                // For new emails, use the label (the typed email)
                field.onChange(selectedOption?.label || null);
              } else {
                // For existing emails from parent SPOC, use their email (or null)
                const selectedSpoc = parentSpocList[selectedOption?.value];
                const emailValue = selectedSpoc?.email || null;
                field.onChange(emailValue);
              }
              
              const isValid = await trigger(`account_contact.${index}.email`);

              if (!isValid) {
                // Don't clear the email field, just mark as not new
                setIsNewAccountContact(prev => editArrayValue(prev, index, false));
                // Only clear first/last name if it was an existing contact
                if (!isNewEmail) {
                  editFirstAndLastName(index, '', '');
                }
              } else {
                // remove email from current list if it was selected from the list
                if (selectedOption?.label && !isNewEmail) {
                  setAvailableEmailOptions(prev => prev.filter(item => item.value !== selectedOption?.value));
                }
              }
              
              setSelectedEmails(prev => editArrayValue(prev, index, {label: selectedOption?.label, value: selectedOption?.value}));
            }}
            contactField={contactField}
            isNewAccountContact={isNewAccountContact}
            control={control}
            getValues={getValues}
            errors={errors}
            handleCheckBox={handleCheckBox}
            handleCheckBoxUpload={handleCheckBoxUpload}
            selectedCanUploadCheckBoxIndex={selectedCanUploadCheckBoxIndex}
            

            removeAccountContact={(index) => {
              setIsNewAccountContact(prev => prev.filter((_, idx) => idx !== index));
              
              if (emailOptions.includes(selectedEmails[index].label)) {
                setAvailableEmailOptions(prev => {
                  // Check if it already exists before adding
                  const alreadyExists = prev.some(item => item.value === selectedEmails[index].value);
                  if (!alreadyExists) {
                    return [...prev, selectedEmails[index]];
                  }
                  return prev;
                });
              }
              
              setSelectedEmails(prev => prev.filter((_, idx) => idx != index));
              contactField.remove(index);
            }}

            fieldValue={(field) => {
              return field.value ? { label: field.value, value: emailToIndexMapping[field.value] } : null
            }}

            emailOptions={availableEmailOptions}
            // selectedEmails={getValues().account_contact.map(el => el.email)}
            canCreateWithoutEmail={true}
            />;
  }

  const promiseOptions = async inputValue => {
    return accountsRepository.getSpecialtyOptions({ search: inputValue });
  };
  const renderSpecialtySelect = () => {
    return (
      <SearchDropDown
        dataValue={dataValue}
        setDataValue={setDataValue}
        promiseOptions={promiseOptions}
      />
    );
  };
  function renderSpecialtiesForModal(inputProps) {
    return (
      <SelectFormModal
        {...inputProps}
        title={'Add Health System'}
        addNewText={'Other Specialty Name'}
        subTitle={'Select Account Specialties'}
        isFetching={accountsContext.isFetchingSpecialtyOption}
        isCreating={accountsContext.isCreatingSpecialtyOption}
        onCreateNewOption={accountsContext.createSpecialtyOption}
        renderSpecialtySelect={renderSpecialtySelect}
        dataValue={dataValue}
      />
    );
  }

  function renderInsuranceFormModal(inputProps) {
    return (
      <SelectFormModal
        {...inputProps}
        title={'Add Health System'}
        addNewText={'Add Other Insurance'}
        subTitle={'Select Insurance'}
        isFetching={accountsContext.isFetchingInsurance}
        isCreating={accountsContext.isCreatingInsurance}
        onCreateNewOption={accountsContext.createInsuranceOption}
      />
    );
  }

  function renderEHRFormModal(inputProps) {
    return (
      <SelectFormModal
        {...inputProps}
        title={'Add Health System'}
        subTitle={'Select EHR'}
        addNewText={'Add Other EHR'}
        isFetching={accountsContext.isFetchingEHR}
        isCreating={accountsContext.isCreatingEHR}
        onCreateNewOption={accountsContext.createEHROptions}
      />
    );
  }

  const onModalClose = () => {
    onClose();
    reset({
      name: '',
      // address: '',
      patients_per_month: '',
      account_contact: [createNewContact()],
    });
    resetAccountContactMetadata();
  };

  return (
    <React.Fragment>
      <Modal
        size={'4xl'}
        isOpen={isOpen}
        onClose={onModalClose}
        isCentered
        blockScrollOnMount={blockScrollOnMount ?? true}
      >
        <ModalOverlay style={{ backgroundColor: '#001A41' }} />
        {accountsContext.isGettingHospitalData ? (
          <ModalContent width={'100'}>
            <Spinner size="xl" />
          </ModalContent>
        ) : (
          <ModalContent mx={4} px={8}>
            <ModalHeader
              fontFamily={FONT_FAMILY.baiJamurjee}
              textAlign={'center'}
            >
              {!hospitalId ? 'Add Hospital' : 'Edit Hospital'}
            </ModalHeader>
            <Box
              style={{
                zIndex: '1',
              }}
              position={'absolute'}
              top={-10}
              right={-3}
            >
              <Circle>
                <ModalCloseButton
                  size={'sm'}
                  style={{ color: AppColors.white }}
                  backgroundColor={'#8894A6'}
                  p={3}
                  onClick={onModalClose}
                  borderRadius={50}
                />
              </Circle>
            </Box>
            <ModalBody pb={6}>
              <SimpleGrid
                columns={{ sm: 1, md: 2, lg: 2 }}
                spacing={GRID_SPACING}
                mb={GRID_SPACING}
              >
                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    Hospital Name
                  </FormLabel>
                  <FormTextInput
                    name="name"
                    control={control}
                    height={'50px'}
                    placeholder={'Hospital Name'}
                    leftIcon={
                      <HealthSystemIcon style={{ marginTop: '10px' }} />
                    }
                    size="md"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    State
                  </FormLabel>

                  <FormCQSelect
                    control={control}
                    options={STATES}
                    value={STATES.find(
                      state => state.value === hsAddress?.split(',')[1]?.trim()
                      // hospitalState?.split(',')[1]?.trim()
                    )}
                    onChange={e => {
                      setHospitalState(e.label);
                      setValue('address', e.label);
                      setHsState(e.label);
                    }}
                    styles={{
                      control: styles => ({
                        ...styles,
                        height: '50px',
                        borderRadius: '5px',
                        border: `1px solid rgba(0,0,0,0.1)`,
                        zIndex: 99,
                      }),
                      menu: (provided, state) => ({
                        ...provided,
                        zIndex: 9999999,
                      }),
                    }}
                    id="state"
                    name="state"
                    placeholder="Select State"
                    variant="primary"
                    isSearchable={true}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    Specialties
                  </FormLabel>
                  <FormMultiSelectModal
                    control={control}
                    name={'specialties'}
                    placeholder={'Ex. Cardiology'}
                    options={accountsContext.specialtyOptions}
                    renderFormModal={renderSpecialtiesForModal}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    Insurance
                  </FormLabel>
                  <FormMultiSelectModal
                    control={control}
                    name={'insurance'}
                    placeholder={'None'}
                    options={accountsContext.insuranceOptions}
                    renderFormModal={renderInsuranceFormModal}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    EHR
                  </FormLabel>
                  <FormMultiSelectModal
                    control={control}
                    name={'ehr'}
                    placeholder={'None'}
                    options={accountsContext.ehrOptions}
                    renderFormModal={renderEHRFormModal}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize={'xs'} fontWeight={'bold'}>
                    No. of Patients per month
                  </FormLabel>
                  <FormTextInput
                    name="patients_per_month"
                    control={control}
                    height={'50px'}
                    placeholder={'Type here'}
                    leftIcon={
                      <PersonIcon
                        style={{ width: 16, height: 16, marginTop: '10px' }}
                      />
                    }
                    size="md"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize={'xs'} fontWeight={'bold'} htmlFor="add-hosp-account-form">
                  Account Contact
                </FormLabel>
                <Box>
                  <Box
                    maxHeight={195}
                    overflow="scroll"
                    overflowX={'hidden'}
                    ref={scrollContainerRef}
                    sx={{
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        boxShadow: 'inset 0 0 4px lightGrey',
                        width: '4px',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'grey',
                        borderRadius: '4px',
                      },
                    }}
                  >
                    {renderContacts()}
                  </Box>
                  <Box>
                    {renderAddIcon({
                      onClick: () => {
                        const currentContacts = getValues("account_contact");
                        if (contactField.fields.length === 1) {
                          const updatedContacts = [currentContacts[0], createNewContact()];
                          reset({
                            ...getValues(),
                            account_contact: updatedContacts,
                          });
                        } else {
                          contactField.append(createNewContact());
                        }
                        setIsNewAccountContact(prev => [...prev, false]);
                        setSelectedEmails(prev => [...prev, {label: null, value: null}]);
                        setSelectedCanUploadCheckBoxIndex(prev => [...prev, contactField.fields.length]);
                      },
                    })}
                  </Box>
                </Box>
              </FormControl>
            </ModalBody>

            <ModalFooter justifyContent={'center'}>
              <Button
                mr={3}
                borderRadius={'full'}
                px={12}
                height={'50px'}
                borderWidth={2}
                borderColor={AppColors.secondary}
                onClick={onModalClose}
                variant={'outline'}
              >
                <Text fontWeight={'normal'} fontSize={'sm'}>
                  Cancel
                </Text>
              </Button>
              <Button
                bgColor={AppColors.primary}
                borderRadius={'full'}
                px={12}
                height={'50px'}
                isLoading={accountsContext.isCreatingHospital}
                loadingText={'Creating'}
                spinnerPlacement={'end'}
                onClick={handleSubmit(submitAddAccount)}
              >
                <Text color={'white'} fontSize={'sm'}>
                  {!hospitalId ? 'Add' : 'Update'}
                </Text>
              </Button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </React.Fragment>
  );
};

export default AddHospitalForm;
